'use client';

import { Checkbox } from '@/components/inputs/Checkbox';
import { Button } from '@/components/ui/Button';
import { ChevronDownIcon } from '@/components/ui/icons';
import { cn } from '@/lib/ui/cn';
import { formatDateInput } from '@/utils/formatDateInput';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { GENDER_OPTIONS } from './BoaForm.constants';
import { boaFormSchema, type BoaFormValues } from './BoaForm.schema';
import type { BoaFormProps } from './BoaForm.types';
import { Field, inputClass } from './Field';

export const BoaForm = ({ loading, errorMessage, onSubmit }: BoaFormProps) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<BoaFormValues>({
    resolver: zodResolver(boaFormSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      dob: '',
      gender: '',
      sms_opt_in: false,
    },
  });

  const gender = useWatch({ control, name: 'gender' });

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className='grid grid-cols-1 gap-x-10 gap-y-6 sm:grid-cols-2'>
        <Field
          id='first_name'
          label='First Name'
          error={errors.first_name?.message}
        >
          {({ errorId, invalid }) => (
            <input
              id='first_name'
              type='text'
              placeholder='Your First Name'
              aria-invalid={invalid}
              aria-describedby={errorId}
              className={cn(inputClass, invalid && 'border-sh-orange')}
              {...register('first_name')}
            />
          )}
        </Field>
        <Field
          id='last_name'
          label='Last Name'
          error={errors.last_name?.message}
        >
          {({ errorId, invalid }) => (
            <input
              id='last_name'
              type='text'
              placeholder='Your Last Name'
              aria-invalid={invalid}
              aria-describedby={errorId}
              className={cn(inputClass, invalid && 'border-sh-orange')}
              {...register('last_name')}
            />
          )}
        </Field>
        <Field id='email' label='Email' error={errors.email?.message}>
          {({ errorId, invalid }) => (
            <input
              id='email'
              type='email'
              placeholder='Add Email'
              aria-invalid={invalid}
              aria-describedby={errorId}
              className={cn(inputClass, invalid && 'border-sh-orange')}
              {...register('email')}
            />
          )}
        </Field>
        <Field id='phone' label='Phone' error={errors.phone?.message}>
          {({ errorId, invalid }) => (
            <input
              id='phone'
              type='tel'
              placeholder='Add Phone Number'
              aria-invalid={invalid}
              aria-describedby={errorId}
              className={cn(inputClass, invalid && 'border-sh-orange')}
              {...register('phone')}
            />
          )}
        </Field>
        <Field id='dob' label='Date of Birth' error={errors.dob?.message}>
          {({ errorId, invalid }) => (
            <Controller
              control={control}
              name='dob'
              render={({ field: { value, onChange, onBlur, ref, name } }) => (
                <input
                  id='dob'
                  ref={ref}
                  name={name}
                  type='text'
                  inputMode='numeric'
                  maxLength={10}
                  placeholder='Date of Birth (MM/DD/YYYY)'
                  value={value}
                  onChange={e => onChange(formatDateInput(e.target.value))}
                  onBlur={onBlur}
                  aria-invalid={invalid}
                  aria-describedby={errorId}
                  className={cn(inputClass, invalid && 'border-sh-orange')}
                />
              )}
            />
          )}
        </Field>
        <Field id='gender' label='Gender' error={errors.gender?.message}>
          {({ errorId, invalid }) => (
            <div className='relative'>
              <select
                id='gender'
                aria-invalid={invalid}
                aria-describedby={errorId}
                className={cn(
                  inputClass,
                  'appearance-none pr-10',
                  !gender && 'text-[#9a9a9a]',
                  invalid && 'border-sh-orange'
                )}
                {...register('gender')}
              >
                <option value='' disabled hidden>
                  --None--
                </option>
                {GENDER_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className='text-sh-navy pointer-events-none absolute top-1/2 right-4 h-2 w-3 -translate-y-1/2' />
            </div>
          )}
        </Field>
        <div className='sm:col-span-2'>
          <Controller
            control={control}
            name='sms_opt_in'
            render={({ field: { value, onChange, name } }) => (
              <Checkbox
                id='sms_opt_in'
                name={name}
                checked={!!value}
                onChange={e => onChange(e.target.checked)}
                label={
                  <span className='text-sh-navy font-sans text-[12px] leading-[1.4] font-normal tracking-[0.016em]'>
                    By checking this box, I consent to receive marketing,
                    customer care, public service announcements, and account
                    notifications from Sollis Health. Reply STOP to opt-out;
                    Reply HELP for support; Message & data rates may apply;
                    Messaging frequency may vary. Visit our{' '}
                    <Link
                      href='https://www.sollishealth.com/privacy'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-sh-orange underline'
                    >
                      Privacy Policy
                    </Link>{' '}
                    and{' '}
                    <Link
                      href='https://www.sollishealth.com/legal'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-sh-orange underline'
                    >
                      Terms of Service
                    </Link>{' '}
                    for more information.
                  </span>
                }
              />
            )}
          />
        </div>
      </div>

      {errorMessage && (
        <p role='alert' className='text-sh-orange mt-6 font-sans text-sm'>
          {errorMessage}
        </p>
      )}

      <Button
        type='submit'
        variant='dark'
        disabled={loading}
        className='hover:bg-sh-navy mt-6.25 h-11.25 w-auto rounded-sm border border-[#101726] bg-[#2b3944] px-7.5 text-[16px] font-normal tracking-widest text-white uppercase opacity-100 md:w-auto md:text-[16px]'
      >
        {loading ? 'Submitting…' : 'Submit'}
      </Button>
    </form>
  );
};
